import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../shared/AuthContext";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { getBranches } from "../../utils/utils";
import "./Header.css";

const Header = () => {
  const { data, roleName, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const name = data?.name || "User";
  const displayRole = roleName || "Staff";
  const isSuperAdmin = String(roleName || "").toLowerCase().trim() === "superadmin";

  const [currentBranchName, setCurrentBranchName] = useState(
    data?.fk_staff_details_belongsTo_branch_details_branchId?.branchName || data?.branchName || ""
  );

  useEffect(() => {
    if (isSuperAdmin) {
      setCurrentBranchName("Main Branch");
      return;
    }

    if (data?.fk_staff_details_belongsTo_branch_details_branchId?.branchName) {
      setCurrentBranchName(data.fk_staff_details_belongsTo_branch_details_branchId.branchName);
      return;
    }
    if (data?.branchName) {
      setCurrentBranchName(data.branchName);
      return;
    }

    const targetBranchId = Array.isArray(data?.branchId) ? data.branchId[0] : data?.branchId;
    if (targetBranchId) {
      getBranches()
        .then((branches) => {
          const match = branches.find((b) => String(b.value) === String(targetBranchId));
          if (match) {
            setCurrentBranchName(match.label);
          }
        })
        .catch(() => {});
    }
  }, [data, isSuperAdmin, roleName]);

  const resolvedBranchName = isSuperAdmin
    ? "Main Branch"
    : (currentBranchName || data?.fk_staff_details_belongsTo_branch_details_branchId?.branchName || "");

  const getBreadcrumbs = (path, search) => {
    const queryParams = new URLSearchParams(search);
    const type = queryParams.get("type");

    const mapping = {
      "/dashboard": ["Dashboard"],
      "/customers": ["Master", "Customers"],
      "/branches": ["Master", "Branches"],
      "/centers": ["Master", "Centers"],
      "/staff": ["Master", "Staff"],
      "/accounts": ["Master", "Accounts"],
      "/fixeddeposite": ["Master", "Fixed Deposit"],
      "/Chitfund": ["Master", "Chitfund"],
      "/balance-book": ["Master", "Balance Book"],
      "/businessLoan": ["Master", "Business Loan"],
      "/memberdetails": ["Master", "Member Details"],
      "/roles": ["Master", "Roles"],
      "/funding-agencies": ["Master", "Funding Agencies"],
      "/permissions": ["Master", "Permissions"],
      "/swaping_field_officer": ["Master", "Swaping Field Officer"],

      "/dailyreport": ["Reports", "Daily Report"],
      "/daily-report": ["Reports", "Daily Report"],
      "/dayendprocess": ["Reports", "Day End Process"],
      "/dayendprocess-v2": ["Reports", "Day End Process (New)"],
      "/weeklyreport": ["Reports", "Weekly Report"],
      "/chitfundreport": ["Reports", "Chitfund Report"],
      "/fixeddepositereport": ["Reports", "FD Report"],
      "/businessloanreport": ["Reports", "Business Loan Report"],
      "/DailyCollectionReport": ["Reports", "Daily Collection Report"],
      "/DisbursedAmountReport": ["Reports", "Disbursed Amount Report"],
      "/CroCollectionsReport": ["Reports", "Cro Collections Report"],
      "/loandemandreport": ["Reports", "Loan Demand Report"],
      "/savingsreport": ["Reports", "Savings Report"],
      "/demand_collection_report": ["Reports", "Demand Collection Report"],
      "/sfu_report": ["Reports", "SFU Report"],
      "/depositeandwithdraw_report": ["Reports", "Deposit & Withdrawal Report"],
      "/future-demand-report": ["Reports", "Future Demand"],
      "/overdue-report": ["Reports", "Overdue"],
      "/par-report": ["Reports", "PAR"],
      "/security-deposit-report": ["Reports", "Security Deposit"],
      "/interest-report": ["Reports", "Interest"],
      "/foreclosure-report": ["Reports", "Foreclosure"],
      "/loan-outstanding-report": ["Reports", "Loan Outstanding"],
      "/master-data-report": ["Reports", "Master Data"],

      "/generalledger": ["Ledger", "General Ledger"],
      "/daybook": ["Ledger", "Day Book"],
      "/cashbook": ["Ledger", "Cash Book"],
      "/bankbook": ["Ledger", "Bank Book"],
      "/balance_sheet": ["Ledger", "Balance Sheet"],
      "/ledger_section": ["Ledger", "Ledger Section"],
      "/accountGroup": ["Ledger", "Account Group"],
      "/ledgerGroup": ["Ledger", "Ledger Group"],
      "/ledgerReport": ["Ledger", "Ledger Report"],
      "/receipts": ["Ledger", "Receipts"],
      "/payments": ["Ledger", "Payments"],
      "/balance-sheet": ["Ledger", "Balance Sheet"],

      "/uploadmemberdetails": ["Tools", "Upload Member Details"],
      "/uploadaccount": ["Tools", "Upload Account Statement"],
      "/accountcreation": ["Tools", "Accounts Creation"],
    };

    // Check direct mapping first
    if (mapping[path]) {
      return mapping[path];
    }

    // Dynamic Add/Edit/View pages
    const addEditMappings = {
      "/addEditCustomer": ["Master", "Customers"],
      "/addEditBusinessLoan": ["Master", "Business Loan"],
      "/addEditBranch": ["Master", "Branches"],
      "/addEditCenter": ["Master", "Centers"],
      "/addEditRole": ["Master", "Roles"],
      "/addEditStaff": ["Master", "Staff"],
      "/addEditAccount": ["Master", "Accounts"],
      "/addEditDeposit": ["Master", "Deposits"],
      "/addEditWithdraw": ["Master", "Withdrawals"],
      "/addEditFundingAgency": ["Master", "Funding Agencies"],
    };

    if (addEditMappings[path]) {
      return [...addEditMappings[path], type || "Add"];
    }

    // Fallback parser for any other segment
    const segment = path.replace("/", "").replace("-", " ");
    const capitalized = segment.charAt(0).toUpperCase() + segment.slice(1);
    return [capitalized || "Home"];
  };

  const breadcrumbs = getBreadcrumbs(location.pathname, location.search);

  const handleLogoutClick = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="app-header">
      {/* Left side: Tab navigation breadcrumbs */}
      <div className="header-left">
        <div className="header-breadcrumbs">
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span className="breadcrumb-separator">&gt;</span>}
              <span className={idx === breadcrumbs.length - 1 ? "breadcrumb-child" : "breadcrumb-parent"}>
                {crumb}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Right side: Branch, User info, Logout */}
      <div className="header-right">
        {/* Branch Name */}
        <div className="header-branch-selector">
          <AccountBalanceIcon className="header-branch-icon" />
          <span className="header-branch-text">{resolvedBranchName || (isSuperAdmin ? "Main Branch" : "")}</span>
          <svg
            className="header-dropdown-chevron"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* User profile details */}
        <div className="header-user-profile">
          <AccountCircleIcon className="header-user-avatar" />
          <div className="header-user-info">
            <span className="header-user-name">{name}</span>
            <span className="header-user-role">{displayRole}</span>
          </div>
        </div>

        {/* Logout Button */}
        <button className="header-logout-button" onClick={handleLogoutClick}>
          <ExitToAppIcon className="header-logout-icon" />
          <span>Logout</span>
        </button>
      </div>
    </header>
  );
};

export default Header;
